import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { publicApi, API } from "@/lib/api";
import { STATUS_META } from "@/lib/constants";
import PaymentSelector from "@/components/PaymentSelector";
import { CreditCard, Eye, X, FileText, MapPin, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { formatPickupLocal } from "@/lib/datetime";

export default function Lookup() {
  const nav = useNavigate();
  // Pre-fill from URL params so SMS / email deep links land on a one-tap submit.
  // Accepts both ?email= (legacy) and ?contact= (new email-or-phone field).
  const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const [code, setCode] = useState(urlParams?.get("code")?.toUpperCase() || "");
  const [contact, setContact] = useState(urlParams?.get("contact") || urlParams?.get("email") || urlParams?.get("phone") || "");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPay, setShowPay] = useState(false);

  // Smart-detect: if input contains '@' treat as email, else as phone.
  const isEmail = (s) => /@/.test((s || "").trim());

  const search = async () => {
    if (!code || !contact) return toast.error("Confirmation code and email or phone required");
    setBusy(true);
    try {
      const r = await publicApi.get(`/bookings/lookup`, { params: { code, contact } });
      setResult(r.data);
    } catch (e) {
      toast.error("No reservation found");
      setResult(null);
    } finally { setBusy(false); }
  };

  const refreshResult = async () => {
    if (!result) return;
    try {
      // Use whatever the user originally entered to refresh
      const r = await publicApi.get(`/bookings/lookup`, {
        params: { code: result.confirmation_code, contact },
      });
      setResult(r.data);
    } catch { /* ignore */ }
  };

  const textPdfLink = async () => {
    if (!result?.confirmation_code) return;
    try {
      const r = await publicApi.post(`/bookings/${result.confirmation_code}/text-link`, { contact });
      if (r.data?.status === "MOCKED") {
        toast.success("Link prepared (SMS in test mode — not actually sent).");
      } else {
        toast.success(`Sent to ${r.data?.to || "your phone"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send SMS");
    }
  };

  // Auto-submit if landed via SMS/email deep-link with both code+contact prefilled
  const autoTried = useRef(false);
  useEffect(() => {
    if (!autoTried.current && code && contact && !result && !busy) {
      autoTried.current = true;
      search();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canPay = result
    && result.payment_status !== "paid"
    && result.payment_status !== "pending"
    && !["cancelled"].includes(result.status);

  return (
    <div data-testid="lookup-page" className="public-light bg-[#050505] min-h-screen text-[#F5F2EB]">
      <Navbar/>
      <section className="pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-xl mx-auto">
          <div className="overline mb-3">Find reservation</div>
          <h1 className="font-[Outfit] text-5xl md:text-6xl tracking-tighter font-light leading-none mb-3">
            Quick <span className="italic font-normal">lookup.</span>
          </h1>
          <p className="text-[#A1A1AA] mb-10 text-sm">Enter the DLX-XXXXXX confirmation code from your email or text, plus the email <span className="italic">or</span> phone number you used to book.</p>

          <div className="border border-white/10 bg-[#0A0A0A] p-8 space-y-6">
            <div>
              <label className="field-label">Confirmation code</label>
              <input data-testid="lookup-code" className="field-input uppercase" value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="DLX-AB12CD"/>
            </div>
            <div>
              <label className="field-label">Email or phone</label>
              <input
                data-testid="lookup-contact"
                type="text"
                inputMode={isEmail(contact) ? "email" : "tel"}
                autoComplete="email tel"
                className="field-input"
                value={contact}
                onChange={e => setContact(e.target.value)}
                placeholder="you@email.com  ·  or  (414) 555-1234"
              />
              <div className="text-[10px] text-[#71717A] mt-1.5 tracking-wide">
                Use whichever you provided when booking — we auto-detect.
              </div>
            </div>
            <button data-testid="lookup-search" disabled={busy} onClick={search} className="btn-gold w-full">{busy ? "Searching…" : "Find Reservation"}</button>
          </div>

          {result && (
            <div data-testid="lookup-result" className="mt-10 border border-[#D4AF37]/40 bg-[#0A0A0A] p-8">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="overline">{result.confirmation_code}</div>
                  <div className="font-serif text-2xl mt-1">{result.vehicle_name}</div>
                </div>
                <span className="status-pill" style={{ color: STATUS_META[result.status]?.color }}>{STATUS_META[result.status]?.label}</span>
              </div>
              <div className="space-y-2 text-sm">
                <Row k="Passenger" v={result.customer_name}/>
                <Row k="Pickup" v={formatPickupLocal(result.pickup_datetime)}/>
                <Row k="From" v={result.pickup_address}/>
                {result.dropoff_address && <Row k="To" v={result.dropoff_address}/>}
                {result.assigned_driver_name && <Row k="Chauffeur" v={result.assigned_driver_name}/>}
                <Row k="Total" v={`$${result.fare.toFixed(2)}`} gold/>
                <Row k="Payment" v={
                  result.payment_status === "paid"
                    ? `Paid · ${(result.payment_method || "card").replace(/_/g, " ")}`
                    : result.payment_status === "pending"
                      ? "Pending confirmation"
                      : "Pay later"
                }/>
              </div>

              {/* Primary action — Pay now (only when payment is still owed) */}
              {canPay && (
                <button
                  data-testid="lookup-pay-now"
                  onClick={() => setShowPay(true)}
                  className="btn-gold w-full mt-6 justify-center"
                ><CreditCard size={14}/>Add card · Pay now</button>
              )}

              {/* Deep View — always available so customers can see full trip details,
                  chauffeur info, pickup grace, timeline, map, etc. */}
              <a
                data-testid="lookup-deep-view"
                href={`/track?code=${result.confirmation_code}&contact=${encodeURIComponent(contact)}`}
                className={`${canPay ? "btn-ghost" : "btn-gold"} w-full mt-3 justify-center`}
              ><Eye size={14}/>Deep view</a>

              {["assigned", "en_route", "in_progress"].includes(result.status) && (
                <a
                  data-testid="lookup-track"
                  href={`/track?code=${result.confirmation_code}&contact=${encodeURIComponent(contact)}`}
                  className="btn-ghost w-full mt-3 justify-center"
                ><MapPin size={14}/>Track this ride live</a>
              )}

              <div className="flex flex-wrap gap-3 mt-6">
                <a
                  data-testid="lookup-trip-pdf"
                  href={`${API}/bookings/${result.confirmation_code}/trip-sheet.pdf?contact=${encodeURIComponent(contact)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost flex-1 justify-center"
                ><FileText size={14}/>Trip Sheet PDF</a>
                {result.payment_status === "paid" && (
                  <a
                    data-testid="lookup-receipt-pdf"
                    href={`${API}/bookings/${result.confirmation_code}/receipt.pdf?contact=${encodeURIComponent(contact)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-ghost flex-1 justify-center"
                  ><FileText size={14}/>Payment Receipt PDF</a>
                )}
                <button
                  type="button"
                  data-testid="lookup-text-link"
                  onClick={textPdfLink}
                  className="btn-ghost flex-1 justify-center"
                ><MessageSquare size={14}/>Text me the link</button>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer/>

      {/* Pay-now modal — anyone with the confirmation code + email can update
          the payment method (matches dispatcher capability for self-serve). */}
      {showPay && result && (
        <div
          data-testid="lookup-pay-modal"
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowPay(false)}
        >
          <div
            className="bg-[#050505] border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 relative text-left text-[#F5F2EB]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="lookup-pay-modal-close"
              onClick={() => setShowPay(false)}
              className="absolute top-4 right-4 text-[#A1A1AA] hover:text-white"
              aria-label="Close"
            ><X size={18}/></button>
            <div className="overline mb-1">Pay for your ride</div>
            <div className="font-serif text-2xl text-[#D4AF37] mb-4">{result.confirmation_code}</div>
            <PaymentSelector
              booking={result}
              onPaid={() => { setShowPay(false); refreshResult(); toast.success("Payment updated"); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v, gold }) {
  return (
    <div className="flex justify-between py-2 border-b border-white/5 last:border-b-0 gap-4">
      <span className="text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] shrink-0">{k}</span>
      <span className={`text-right ${gold ? 'text-[#D4AF37] font-serif text-lg' : 'text-[#F5F2EB]'}`}>{v}</span>
    </div>
  );
}
